import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
// Minimal coordinate presence
const CoordinateGrid = () => <div className="absolute inset-0 w-full h-full opacity-[0.015] pointer-events-none" style={{
  backgroundImage: 'linear-gradient(#F5F5F5 0.5px, transparent 0.5px), linear-gradient(90deg, #F5F5F5 0.5px, transparent 0.5px)',
  backgroundSize: '80px 80px'
}} aria-hidden="true" data-id="element-4407" />;
export function SelfOrientationInputs() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const product = searchParams.get('product') || 'self';
  const [formData, setFormData] = useState({
    fullName: '',
    dateOfBirth: '',
    timeOfBirth: '',
    timeKnown: 'exact',
    placeOfBirth: '',
    country: '',
    year: '',
    month: '',
    day: '',
    hour: '12',
    minute: '00',
    period: 'AM'
  });
  const currentYear = new Date().getFullYear();
  const years = Array.from({
    length: currentYear - 1940
  }, (_, i) => currentYear - i);
  const months = [{
    value: '01',
    label: 'January'
  }, {
    value: '02',
    label: 'February'
  }, {
    value: '03',
    label: 'March'
  }, {
    value: '04',
    label: 'April'
  }, {
    value: '05',
    label: 'May'
  }, {
    value: '06',
    label: 'June'
  }, {
    value: '07',
    label: 'July'
  }, {
    value: '08',
    label: 'August'
  }, {
    value: '09',
    label: 'September'
  }, {
    value: '10',
    label: 'October'
  }, {
    value: '11',
    label: 'November'
  }, {
    value: '12',
    label: 'December'
  }];
  const getDaysInMonth = (year: string, month: string) => {
    if (!year || !month) return 31;
    return new Date(parseInt(year), parseInt(month), 0).getDate();
  };
  const updateDateOfBirth = (year: string, month: string, day: string) => {
    if (year && month && day) {
      const dateString = `${year}-${month}-${day}`;
      setFormData(prev => ({
        ...prev,
        year,
        month,
        day,
        dateOfBirth: dateString
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        year,
        month,
        day,
        dateOfBirth: ''
      }));
    }
  };
  const convertTo24Hour = (hour: string, period: string): string => {
    let h = parseInt(hour);
    if (period === 'PM' && h !== 12) h += 12;
    if (period === 'AM' && h === 12) h = 0;
    return h.toString().padStart(2, '0');
  };
  const updateTimeOfBirth = (hour: string, minute: string, period: string) => {
    const hour24 = convertTo24Hour(hour, period);
    const timeString = `${hour24}:${minute}`;
    setFormData(prev => ({
      ...prev,
      hour,
      minute,
      period,
      timeOfBirth: timeString
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.fullName || !formData.dateOfBirth || !formData.placeOfBirth || !formData.country) {
      return;
    }
    if ((formData.timeKnown === 'exact' || formData.timeKnown === 'approximate') && !formData.timeOfBirth) {
      return;
    }
    const mockReportId = 'demo-' + Date.now();
    navigate('/generating-report', {
      state: {
        reportId: mockReportId,
        product
      }
    });
  };
  const getConfig = () => {
    switch (product) {
      case 'work':
        return {
          title: 'Work System Diagnostic',
          icon: '💼',
          framing: 'You are about to run a structural diagnostic. This does not give advice. It shows patterns.'
        };
      case 'child':
        return {
          title: 'Child System Map',
          icon: '🧒',
          framing: 'You are about to run a structural diagnostic. This does not give advice. It shows patterns.'
        };
      case 'relationship':
        return {
          title: 'Relationship Dynamics Diagnostic',
          icon: '💞',
          framing: 'You are about to run a structural diagnostic. This does not give advice. It shows patterns.'
        };
      default:
        // self
        return {
          title: 'Self System Diagnostic',
          icon: '🧬',
          framing: 'You are about to run a structural diagnostic. This does not give advice. It shows patterns.'
        };
    }
  };
  const config = getConfig();
  return <>
      <Helmet data-id="element-4408">
        <title data-id="element-4409">{config.title} — Scan Input | Lazy Monk Labs</title>
        <meta name="robots" content="noindex, nofollow" data-id="element-4410" />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-4411">
        {/* Navigation */}
        <header data-id="element-4412">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-4413">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-4414">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-4415">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-4416" />
                <span className="text-base font-mono tracking-tight text-gray-400" data-id="element-4417">
                  Lazy Monk Labs
                </span>
              </Link>
              <div className="flex items-center gap-8" data-id="element-4418">
                <Link to="/decode" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-mono" data-id="element-4419">
                  Decoder
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-mono" data-id="element-4420">
                  Contact
                </a>
              </div>
            </div>
          </nav>
        </header>

        <main className="pt-32 pb-16 px-6" data-id="element-4421">
          <div className="max-w-2xl mx-auto" data-id="element-4422">
            {/* 1. HEADER / CONTEXT */}
            <div className="mb-16 space-y-6" data-id="element-4423">
              <div className="flex items-center gap-4" data-id="element-4424">
                <span className="text-4xl" data-id="element-4425">{config.icon}</span>
                <h1 className="text-3xl md:text-4xl font-serif leading-tight" style={{
                fontFamily: 'Georgia, serif'
              }} data-id="element-4426">
                  {config.title}
                </h1>
              </div>

              <p className="text-base text-gray-400 leading-relaxed" data-id="element-4427">
                {config.framing}
              </p>

              <p className="text-sm text-gray-500" data-id="element-4428">
                Enter the details to generate the scan.
              </p>
            </div>

            {/* 2. WHAT THIS USES */}
            <div className="mb-12 p-6 border border-[#2B2B2B] bg-[#111214]" data-id="element-4429">
              <p className="text-sm text-gray-400 leading-relaxed" data-id="element-4430">
                This scan uses birth details to map recurring patterns and inner
                structure.
                <br data-id="element-4431" />
                It does not predict the future. It does not tell you what to do.
                <br data-id="element-4432" />
                <br data-id="element-4433" />
                <span className="text-gray-500" data-id="element-4434">
                  The details are only used to generate this diagnostic.
                </span>
              </p>
            </div>

            {/* 3. INPUT SECTION - THE INSTRUMENT PANEL */}
            <form onSubmit={handleSubmit} className="space-y-8" data-id="element-4435">
              {/* Name */}
              <div data-id="element-4436">
                <label htmlFor="fullName" className="block text-xs font-mono text-gray-500 uppercase tracking-wider mb-3" data-id="element-4437">
                  Name
                </label>
                <input type="text" id="fullName" value={formData.fullName} onChange={e => setFormData(prev => ({
                ...prev,
                fullName: e.target.value
              }))} placeholder="Full name" required className="w-full px-4 py-4 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-gray-600 focus:border-[#64748B] focus:outline-none transition-colors" data-id="element-4438" />
              </div>

              {/* Date of Birth */}
              <div data-id="element-4439">
                <label className="block text-xs font-mono text-gray-500 uppercase tracking-wider mb-3" data-id="element-4440">
                  Date of Birth
                </label>

                {formData.dateOfBirth && <div className="mb-4 p-4 bg-[#111214] border border-[#2B2B2B] text-center" data-id="element-4441">
                    <div className="text-xl font-mono text-gray-400" data-id="element-4442">
                      {months.find(m => m.value === formData.month)?.label}{' '}
                      {formData.day}, {formData.year}
                    </div>
                  </div>}

                <div className="grid grid-cols-3 gap-3" data-id="element-4443">
                  <div data-id="element-4444">
                    <select id="month" value={formData.month} onChange={e => updateDateOfBirth(formData.year, e.target.value, formData.day)} className="w-full px-3 py-4 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#64748B] focus:outline-none transition-colors" data-id="element-4445">
                      <option value="" data-id="element-4446">Month</option>
                      {months.map(m => <option key={m.value} value={m.value} data-id="element-4447">
                          {m.label}
                        </option>)}
                    </select>
                  </div>
                  <div data-id="element-4448">
                    <select id="day" value={formData.day} onChange={e => updateDateOfBirth(formData.year, formData.month, e.target.value)} disabled={!formData.month || !formData.year} className="w-full px-3 py-4 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#64748B] focus:outline-none transition-colors disabled:opacity-30" data-id="element-4449">
                      <option value="" data-id="element-4450">Day</option>
                      {Array.from({
                      length: getDaysInMonth(formData.year, formData.month)
                    }, (_, i) => i + 1).map(d => <option key={d} value={d.toString().padStart(2, '0')} data-id="element-4451">
                          {d}
                        </option>)}
                    </select>
                  </div>
                  <div data-id="element-4452">
                    <select id="year" value={formData.year} onChange={e => updateDateOfBirth(e.target.value, formData.month, formData.day)} className="w-full px-3 py-4 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#64748B] focus:outline-none transition-colors" data-id="element-4453">
                      <option value="" data-id="element-4454">Year</option>
                      {years.map(y => <option key={y} value={y} data-id="element-4455">
                          {y}
                        </option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Time of Birth */}
              <div data-id="element-4456">
                <label className="block text-xs font-mono text-gray-500 uppercase tracking-wider mb-3" data-id="element-4457">
                  Time of Birth
                </label>

                <div className="space-y-2 mb-4" data-id="element-4458">
                  {[{
                  value: 'exact',
                  label: 'Exact time'
                }, {
                  value: 'approximate',
                  label: 'Approximate'
                }, {
                  value: 'unknown',
                  label: 'Unknown'
                }].map(option => <label key={option.value} className={`block border p-4 cursor-pointer transition-colors ${formData.timeKnown === option.value ? 'border-[#64748B] bg-[#64748B]/5' : 'border-[#2B2B2B] hover:border-[#64748B]/50'}`} data-id="element-4459">
                      <input type="radio" name="timeKnown" value={option.value} checked={formData.timeKnown === option.value} onChange={e => setFormData(prev => ({
                    ...prev,
                    timeKnown: e.target.value,
                    timeOfBirth: '',
                    hour: '12',
                    minute: '00',
                    period: 'AM'
                  }))} className="sr-only" data-id="element-4460" />
                      <span className="text-sm text-gray-400" data-id="element-4461">
                        {option.label}
                      </span>
                    </label>)}
                </div>

                {(formData.timeKnown === 'exact' || formData.timeKnown === 'approximate') && <div className="mt-6 bg-[#111214] border border-[#2B2B2B] p-8" data-id="element-4462">
                    <div className="text-center mb-8" data-id="element-4463">
                      <div className="text-3xl font-mono text-gray-400" data-id="element-4464">
                        {formData.hour}:{formData.minute} {formData.period}
                      </div>
                      {formData.timeKnown === 'approximate' && <p className="text-xs text-gray-600 mt-2" data-id="element-4465">
                          Your best estimate is fine
                        </p>}
                    </div>

                    <div className="space-y-6" data-id="element-4466">
                      <div data-id="element-4467">
                        <label className="block text-xs font-mono text-gray-600 uppercase tracking-wider mb-3" data-id="element-4468">
                          Hour
                        </label>
                        <div className="grid grid-cols-6 gap-2" data-id="element-4469">
                          {['12', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11'].map(h => <button key={h} type="button" onClick={() => updateTimeOfBirth(h, formData.minute, formData.period)} className={`py-3 text-sm font-mono transition-colors ${formData.hour === h ? 'bg-[#64748B] text-[#F5F5F5]' : 'bg-[#0A0A0A] border border-[#2B2B2B] hover:border-[#64748B]'}`} data-id="element-4470">
                              {h}
                            </button>)}
                        </div>
                      </div>

                      <div data-id="element-4471">
                        <label className="block text-xs font-mono text-gray-600 uppercase tracking-wider mb-3" data-id="element-4472">
                          Minute
                        </label>
                        <div className="grid grid-cols-4 gap-2 mb-2" data-id="element-4473">
                          {['00', '15', '30', '45'].map(m => <button key={m} type="button" onClick={() => updateTimeOfBirth(formData.hour, m, formData.period)} className={`py-3 text-sm font-mono transition-colors ${formData.minute === m ? 'bg-[#64748B] text-[#F5F5F5]' : 'bg-[#0A0A0A] border border-[#2B2B2B] hover:border-[#64748B]'}`} data-id="element-4474">
                              {m}
                            </button>)}
                        </div>
                        <input type="number" min="0" max="59" value={formData.minute} onChange={e => {
                      const val = e.target.value.padStart(2, '0');
                      if (parseInt(val) >= 0 && parseInt(val) <= 59) updateTimeOfBirth(formData.hour, val, formData.period);
                    }} placeholder="Or type exact minute" className="w-full px-4 py-2 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm placeholder-gray-600 focus:border-[#64748B] focus:outline-none transition-colors" data-id="element-4475" />
                      </div>

                      <div data-id="element-4476">
                        <label className="block text-xs font-mono text-gray-600 uppercase tracking-wider mb-3" data-id="element-4477">
                          Period
                        </label>
                        <div className="grid grid-cols-2 gap-2" data-id="element-4478">
                          {['AM', 'PM'].map(p => <button key={p} type="button" onClick={() => updateTimeOfBirth(formData.hour, formData.minute, p)} className={`py-3 text-sm font-mono transition-colors ${formData.period === p ? 'bg-[#64748B] text-[#F5F5F5]' : 'bg-[#0A0A0A] border border-[#2B2B2B] hover:border-[#64748B]'}`} data-id="element-4479">
                              {p}
                            </button>)}
                        </div>
                      </div>
                    </div>
                  </div>}
              </div>

              {/* Place of Birth */}
              <div data-id="element-4480">
                <label htmlFor="placeOfBirth" className="block text-xs font-mono text-gray-500 uppercase tracking-wider mb-3" data-id="element-4481">
                  Place of Birth
                </label>
                <input type="text" id="placeOfBirth" value={formData.placeOfBirth} onChange={e => setFormData(prev => ({
                ...prev,
                placeOfBirth: e.target.value
              }))} placeholder="City or town" required className="w-full px-4 py-4 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-gray-600 focus:border-[#64748B] focus:outline-none transition-colors" data-id="element-4482" />
              </div>

              {/* Country */}
              <div data-id="element-4483">
                <label htmlFor="country" className="block text-xs font-mono text-gray-500 uppercase tracking-wider mb-3" data-id="element-4484">
                  Country
                </label>
                <input type="text" id="country" value={formData.country} onChange={e => setFormData(prev => ({
                ...prev,
                country: e.target.value
              }))} placeholder="Country" required className="w-full px-4 py-4 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-gray-600 focus:border-[#64748B] focus:outline-none transition-colors" data-id="element-4485" />
              </div>

              {/* 4. PRIMARY ACTION */}
              <div className="pt-8 space-y-4" data-id="element-4486">
                <button type="submit" disabled={!formData.fullName || !formData.dateOfBirth || !formData.placeOfBirth || !formData.country || (formData.timeKnown === 'exact' || formData.timeKnown === 'approximate') && !formData.timeOfBirth} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-medium text-sm tracking-widest uppercase hover:bg-gray-200 transition-colors disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-4487">
                  Generate Diagnostic
                </button>

                <p className="text-xs text-gray-600 text-center" data-id="element-4488">
                  This takes a few seconds.
                </p>
              </div>
            </form>

            {/* 5. SERIOUSNESS FOOTNOTE */}
            <div className="mt-16 pt-8 border-t border-[#2B2B2B]" data-id="element-4489">
              <p className="text-xs text-gray-600 text-center" data-id="element-4490">
                Decoder does not give advice. It only shows patterns.
              </p>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="py-12 px-6 border-t border-[#2B2B2B]" data-id="element-4491">
          <div className="max-w-6xl mx-auto" data-id="element-4492">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8" data-id="element-4493">
              <div data-id="element-4494">
                <p className="text-sm text-gray-500 font-mono" data-id="element-4495">
                  Lazy Monk Labs
                </p>
                <p className="text-xs text-gray-600 mt-1" data-id="element-4496">
                  Pattern recognition systems
                </p>
              </div>

              <div className="flex gap-8 text-sm text-gray-500" data-id="element-4497">
                <Link to="/decode" className="hover:text-[#F5F5F5] transition-colors" data-id="element-4498">
                  Decoder
                </Link>
                <Link to="/learnmore/founder" className="hover:text-[#F5F5F5] transition-colors" data-id="element-4499">
                  Founder
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="hover:text-[#F5F5F5] transition-colors" data-id="element-4500">
                  Contact
                </a>
              </div>

              <div className="text-xs text-gray-600 font-mono" data-id="element-4501">
                © {new Date().getFullYear()}
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>;
}